import { translate, type Locale } from '@shared/config/i18n'

import { dayjs, DEFAULT_TIMEZONE } from '../libs/dayjs'

/**
 * Date Formatting Utilities
 * 유저 타임존 기반으로 UTC 시간을 변환
 */

const LOCALIZED_DATE_TIME_FORMAT_KEYS = {
  shortDate: 'common.date.monthDay',
  shortDateWithWeekday: 'common.date.monthDayWeekday',
  shortDateTimeWithWeekday: 'common.date.monthDayWeekdayTime',
  time: 'common.date.time',
} as const

// dayjs 로케일 내장 포맷(LocalizedFormat)을 직접 쓰는 항목 — i18n 카탈로그/GCS 언어팩에 의존하지 않는다.
// ko: 2026년 2월 11일 14:00 / en: February 11, 2026 14:00 / ja: 2026年2月11日 14:00
const NATIVE_DAYJS_FORMATS = {
  yearMonthDayTime: 'LL HH:mm',
} as const

export type LocalizedDateTimeFormat = keyof typeof LOCALIZED_DATE_TIME_FORMAT_KEYS | keyof typeof NATIVE_DAYJS_FORMATS

export const formatLocalizedDateTime = (
  unixTimestamp: number,
  format: LocalizedDateTimeFormat,
  tz: string = DEFAULT_TIMEZONE,
  locale: Locale = 'en',
): string => {
  const template =
    format in NATIVE_DAYJS_FORMATS
      ? NATIVE_DAYJS_FORMATS[format as keyof typeof NATIVE_DAYJS_FORMATS]
      : translate(locale, LOCALIZED_DATE_TIME_FORMAT_KEYS[format as keyof typeof LOCALIZED_DATE_TIME_FORMAT_KEYS])

  return dayjs.unix(unixTimestamp).tz(tz).locale(locale).format(template)
}

const formatLocalizedTimeLeftUnit = (value: number, unit: 'day' | 'hour' | 'minute', locale: Locale) => {
  return translate(locale, `common.timeLeft.${unit}`, { count: value })
}

export const calculateLocalizedTimeLeft = (currentTime: number, targetTime: number, locale: Locale): string => {
  const diff = targetTime - currentTime

  if (diff <= 0) {
    return formatLocalizedTimeLeftUnit(0, 'minute', locale)
  }

  const days = Math.floor(diff / (24 * 60 * 60))
  const hours = Math.floor((diff % (24 * 60 * 60)) / (60 * 60))
  const minutes = Math.floor((diff % (60 * 60)) / 60)

  if (days > 0) {
    return `${formatLocalizedTimeLeftUnit(days, 'day', locale)} ${formatLocalizedTimeLeftUnit(hours, 'hour', locale)}`
  } else if (hours > 0) {
    return `${formatLocalizedTimeLeftUnit(hours, 'hour', locale)} ${formatLocalizedTimeLeftUnit(minutes, 'minute', locale)}`
  } else {
    return formatLocalizedTimeLeftUnit(minutes, 'minute', locale)
  }
}

/**
 * Check if class change is allowed (more than 12 hours before)
 */
export const isClassChangeAllowed = (
  classStartTime: number,
  currentTime: number,
  allowChangeTime: number = 60 * 60 * 12, // 12 hours
): boolean => {
  return classStartTime - allowChangeTime >= currentTime
}

/**
 * Check if user can join class (within 10 minutes)
 */
export const canJoinClass = (
  classStartTime: number,
  currentTime: number,
  allowPermissionTime: number = 60 * 10, // 10 minutes
): boolean => {
  return classStartTime - currentTime <= allowPermissionTime
}

/**
 * Get current time as Unix timestamp (UTC)
 */
export const getCurrentSeoulTime = (): number => {
  return dayjs().unix()
}

/**
 * Calculate progress percentage for pre-study
 */
export const calculatePreStudyProgress = (preStudyTime: number, maxTime = 8): number => {
  return Math.min((preStudyTime / maxTime) * 100, 100)
}
